import { z } from "zod";

import { COGNITO_INTERNAL_STAFF_GROUPS } from "@recobra/core";

import { CaseType, CaseWorkflowStep } from "../../case-core";
import {
  DocumentTypeSchema,
  REPAIR_PROOF_DOCUMENT_TYPES,
  type EffectiveDocumentTypeInput,
  effectiveDocumentType,
} from "../../document";

const CaseDocumentRequirementDueStepSchema = z.enum(["analysis", "legal_process", "optional"]);
type CaseDocumentRequirementDueStep = z.infer<typeof CaseDocumentRequirementDueStepSchema>;
type DocumentType = z.infer<typeof DocumentTypeSchema>;

const requirement = (
  dueStep: CaseDocumentRequirementDueStep,
  ...acceptedDocumentTypes: [DocumentType, ...DocumentType[]]
) => ({ dueStep, acceptedDocumentTypes });

/**
 * Any repair paperwork lets the analysis start, while the estimate row stays
 * pending on an invoice or a work order alone so the orçamento is still asked for.
 */
const CASE_DOCUMENT_REQUIREMENTS = {
  third_party_claim: {
    police_report: requirement("analysis", "police_report"),
    accident_photos: requirement("analysis", "accident_photos"),
    repair_proof: requirement("analysis", ...REPAIR_PROOF_DOCUMENT_TYPES),
    repair_estimate: requirement("optional", "repair_estimate"),
    vehicle_document: requirement("legal_process", "vehicle_document"),
    income_proof: requirement("legal_process", "income_proof"),
    vehicle_downtime_declaration: requirement("legal_process", "vehicle_downtime_declaration"),
  },
  road_conditions: {
    police_report: requirement("analysis", "police_report"),
    accident_photos: requirement("analysis", "accident_photos"),
    repair_proof: requirement("analysis", ...REPAIR_PROOF_DOCUMENT_TYPES),
    repair_estimate: requirement("optional", "repair_estimate"),
    vehicle_document: requirement("legal_process", "vehicle_document"),
    income_proof: requirement("legal_process", "income_proof"),
    vehicle_downtime_declaration: requirement("legal_process", "vehicle_downtime_declaration"),
  },
  zero_km_warranty: {
    accident_photos: requirement("analysis", "accident_photos"),
    repair_proof: requirement("analysis", ...REPAIR_PROOF_DOCUMENT_TYPES),
    repair_estimate: requirement("optional", "repair_estimate"),
    service_order: requirement("analysis", "repair_work_order"),
    vehicle_purchase_invoice: requirement("analysis", "vehicle_purchase_invoice"),
    vehicle_document: requirement("legal_process", "vehicle_document"),
    income_proof: requirement("legal_process", "income_proof"),
    vehicle_downtime_declaration: requirement("legal_process", "vehicle_downtime_declaration"),
  },
  routine_maintenance: {
    repair_proof: requirement("analysis", ...REPAIR_PROOF_DOCUMENT_TYPES),
    repair_estimate: requirement("optional", "repair_estimate"),
    service_order: requirement("analysis", "repair_work_order"),
    vehicle_document: requirement("legal_process", "vehicle_document"),
    income_proof: requirement("legal_process", "income_proof"),
    vehicle_downtime_declaration: requirement("legal_process", "vehicle_downtime_declaration"),
  },
  hidden_defect: {
    repair_proof: requirement("analysis", ...REPAIR_PROOF_DOCUMENT_TYPES),
    repair_estimate: requirement("optional", "repair_estimate"),
    service_order: requirement("analysis", "repair_work_order"),
    vehicle_document: requirement("legal_process", "vehicle_document"),
    income_proof: requirement("legal_process", "income_proof"),
    vehicle_downtime_declaration: requirement("legal_process", "vehicle_downtime_declaration"),
  },
  debt_collection: {},
} satisfies Record<CaseType, Record<string, ReturnType<typeof requirement>>>;

export const CaseRequirementExceptionSchema = z.object({
  requirementKey: z.string().min(1),
  approvedAt: z.iso.datetime(),
  approvedBy: z.string().min(1),
  reviewTaskId: z.uuid(),
  mutationId: z.uuid(),
});
export type CaseRequirementException = z.infer<typeof CaseRequirementExceptionSchema>;

const CaseDocumentRequirementStatusSchema = z.object({
  key: z.string().min(1).meta({ visibleTo: "*" }),
  dueStep: CaseDocumentRequirementDueStepSchema.meta({ visibleTo: "*" }),
  acceptedDocumentTypes: z.array(DocumentTypeSchema).min(1).meta({ visibleTo: "*" }),
  satisfied: z.boolean().meta({ visibleTo: "*" }),
  excepted: z.boolean().meta({ visibleTo: "*" }),
  requiredForTarget: z.boolean().meta({ visibleTo: "*" }),
});
export type CaseDocumentRequirementStatus = z.infer<typeof CaseDocumentRequirementStatusSchema>;

const CaseStepFieldRequirementStatusSchema = z.object({
  key: z.string().min(1).meta({ visibleTo: "*" }),
  dueStep: z.enum(["negotiation", "legal_process"]).meta({ visibleTo: "*" }),
  satisfied: z.boolean().meta({ visibleTo: "*" }),
  excepted: z.boolean().meta({ visibleTo: "*" }),
  requiredForTarget: z.boolean().meta({ visibleTo: "*" }),
});
export type CaseStepFieldRequirementStatus = z.infer<typeof CaseStepFieldRequirementStatusSchema>;

export const CaseDocumentRequirementsResponseSchema = z.object({
  state: z.enum(["awaiting_creation_completion", "ready"]).meta({ visibleTo: "*" }),
  caseType: CaseType.meta({ visibleTo: "*" }),
  targetStep: CaseWorkflowStep.meta({ visibleTo: "*" }),
  canAdvance: z.boolean().meta({ visibleTo: "*" }),
  documents: z.array(CaseDocumentRequirementStatusSchema).meta({ visibleTo: "*" }),
  fieldsComplete: z.boolean().meta({ visibleTo: "*" }),
  missingFieldLabels: z.array(z.string()).meta({ visibleTo: "*" }),
  stepFieldRequirements: z.array(CaseStepFieldRequirementStatusSchema).meta({ visibleTo: "*" }),
  missingStepFieldLabels: z.array(z.string()).meta({ visibleTo: "*" }),
  intakeAnalysisStatus: z
    .enum(["pending", "completed", "failed"])
    .nullable()
    .meta({ visibleTo: "*" }),
  primaryCustomerUsername: z
    .string()
    .nullish()
    .meta({ visibleTo: [...COGNITO_INTERNAL_STAFF_GROUPS, "Automation"] }),
});

export const evaluateCaseDocumentRequirements = ({
  caseType,
  targetStep,
  documents: caseDocuments,
  exceptions = [],
}: {
  caseType: CaseType;
  targetStep: CaseWorkflowStep;
  documents: readonly (EffectiveDocumentTypeInput & {
    status: string;
    deletedAt?: string | null;
    notRelatedToCurrentCase?: boolean;
  })[];
  exceptions?: readonly Pick<CaseRequirementException, "requirementKey">[];
}): CaseDocumentRequirementStatus[] => {
  const acceptedTypes = new Set(
    caseDocuments
      .filter(
        (document) =>
          document.status !== "pending" &&
          document.status !== "failed" &&
          !document.deletedAt &&
          !document.notRelatedToCurrentCase,
      )
      .map(effectiveDocumentType)
      .filter((documentType): documentType is string => Boolean(documentType)),
  );
  const exceptionKeys = new Set(exceptions.map(({ requirementKey }) => requirementKey));

  return Object.entries(CASE_DOCUMENT_REQUIREMENTS[caseType]).map(([localKey, definition]) => {
    const key = `${caseType}.${localKey}`;
    const excepted = exceptionKeys.has(key);
    const satisfied =
      excepted || definition.acceptedDocumentTypes.some((type) => acceptedTypes.has(type));

    return {
      ...definition,
      key,
      satisfied,
      excepted,
      requiredForTarget: definition.dueStep === targetStep,
    };
  });
};

/**
 * The requirement a document request stands for: the narrowest one covering
 * every type it asked for, so an orçamento-only request resolves to the
 * estimate row rather than to the broader repair proof.
 */
export const caseDocumentRequirementKeyForRequest = (
  caseType: CaseType,
  request: { documentType: DocumentType; acceptedDocumentTypes?: readonly DocumentType[] },
): string | undefined => {
  const requestedTypes = request.acceptedDocumentTypes ?? [request.documentType];
  const [narrowest] = Object.entries(CASE_DOCUMENT_REQUIREMENTS[caseType])
    .filter(([, { acceptedDocumentTypes }]) =>
      requestedTypes.every((type) => acceptedDocumentTypes.includes(type)),
    )
    .sort(([, a], [, b]) => a.acceptedDocumentTypes.length - b.acceptedDocumentTypes.length);

  return narrowest && `${caseType}.${narrowest[0]}`;
};

export const missingCaseDocumentRequirementKeys = (
  statuses: readonly CaseDocumentRequirementStatus[],
): string[] =>
  statuses
    .filter(({ requiredForTarget, satisfied }) => requiredForTarget && !satisfied)
    .map(({ key }) => key);
